import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Filter, MapPin, Heart, MessageCircle, Star } from 'lucide-react'
import { searchApi } from '../services/api'
import { geocodingService } from '../services/geocoding'
import { createServiceMarker, createAnimalMarker } from '../utils/mapIcons'
import HereMap from '../components/maps/HereMap'
import type { Service, Animal, ServiceSearchQuery, ServiceType, AnimalType } from '../types'
import toast from 'react-hot-toast'

const SearchPage = () => {
  const { t } = useTranslation()
  const [services, setServices] = useState<Service[]>([])
  const [animals, setAnimals] = useState<Animal[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [locationLoading, setLocationLoading] = useState(false)
  const [contentType, setContentType] = useState<'all' | 'services' | 'animals'>('all')
  const [locationFilter, setLocationFilter] = useState<'all' | 'city' | 'neighbourhood'>('all')

  // Filter states
  const [filters, setFilters] = useState<ServiceSearchQuery & { need?: boolean }>({
    service_type: undefined,
    service_mode: undefined,
    animal_type: undefined,
    city: '',
    max_price: undefined,
    max_distance_km: undefined,
    sort_by: 'relevance',
    need: undefined
  })

  const serviceTypes: ServiceType[] = ['pension', 'training', 'walk', 'walk_together', 'mate']
  const animalTypes: AnimalType[] = ['Dog', 'Cat', 'Bird', 'Reptile', 'Aracno']

  useEffect(() => {
    searchAll()
    getUserLocation()
  }, [])

  const getUserLocation = async () => {
    setLocationLoading(true)
    try {
      const location = await geocodingService.getCurrentLocation()
      if (location) {
        setUserLocation(location)
        console.log('User location obtained:', location)
        toast.success(t('search.locationDetected'))
      } else {
        console.log('Location access denied or not available')
        toast.error(t('search.locationUnavailable'))
      }
    } catch (error) {
      console.log('Location access denied:', error)
      toast.error(t('search.locationDenied'))
    } finally {
      setLocationLoading(false)
    }
  }

  const getAllMarkers = () => {
    const markers: any[] = []

    // Add service markers
    if (contentType === 'all' || contentType === 'services') {
      services
        .filter(service => service.latitude && service.longitude)
        .forEach(service => {
          // Add slight randomization for privacy (±500m)
          const lat = service.latitude! + (Math.random() - 0.5) * 0.01
          const lng = service.longitude! + (Math.random() - 0.5) * 0.01
          
          markers.push({
            ...createServiceMarker({
              ...service,
              latitude: lat,
              longitude: lng
            }),
            info: `
              <div style="padding: 8px; max-width: 200px;">
                <h4 style="font-weight: 600; margin-bottom: 4px; color: ${service.need ? '#3B82F6' : '#10B981'};">${service.title}</h4>
                <p style="font-size: 12px; margin-bottom: 2px;"><strong>${service.need ? 'Need' : 'Offer'}:</strong> ${service.description.substring(0, 80)}...</p>
                <p style="font-size: 12px; font-weight: 500; color: #10b981; margin-bottom: 4px;">${service.price_credits} ${t('common.credits')}</p>
                <p style="font-size: 11px; color: #9ca3af;">${service.city}${service.neighborhood ? `, ${service.neighborhood}` : ''} | ${service.provider?.username || 'Unknown'}</p>
              </div>
            `
          })
        })
    }

    // Add animal markers
    if (contentType === 'all' || contentType === 'animals') {
      animals
        .filter(animal => animal.latitude && animal.longitude)
        .forEach(animal => {
          // Add slight randomization for privacy (±500m)
          const lat = animal.latitude! + (Math.random() - 0.5) * 0.01
          const lng = animal.longitude! + (Math.random() - 0.5) * 0.01
        
        markers.push({
          ...createAnimalMarker(animal, userLocation || undefined),
          lat,
          lng,
          info: `
            <div style="padding: 8px; max-width: 200px;">
              <h4 style="font-weight: 600; margin-bottom: 4px; color: #F97316;">${animal.name} (${animal.animal_type})</h4>
              ${animal.breed ? `<p style="font-size: 12px; margin-bottom: 2px;"><strong>Breed:</strong> ${animal.breed}</p>` : ''}
              ${animal.age ? `<p style="font-size: 12px; margin-bottom: 2px;"><strong>Age:</strong> ${animal.age} years</p>` : ''}
              <p style="font-size: 12px; margin-bottom: 4px;">${animal.description?.substring(0, 80) || 'No description'}...</p>
              <p style="font-size: 11px; color: #9ca3af;">${animal.city || 'Location not specified'}${animal.neighborhood ? `, ${animal.neighborhood}` : ''}</p>
            </div>
          `
        })
      })
    }

    return markers
  }

  const searchAll = async () => {
    setIsLoading(true)
    try {
      const searchParams: any = {
        content_type: contentType,
        ...(userLocation && { 
          latitude: userLocation.lat, 
          longitude: userLocation.lng 
        })
      }

      // Only apply regular filters if we're not showing everything
      if (locationFilter !== 'all') {
        // Apply all regular filters when location is restricted
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '' && value !== null) {
            searchParams[key] = value
          }
        })
      } else {
        // When showing everything, only apply content type filters (not location-based ones)
        if (filters.service_type) searchParams.service_type = filters.service_type
        if (filters.animal_type) searchParams.animal_type = filters.animal_type
        if (filters.need !== undefined) searchParams.need = filters.need
        if (filters.max_price) searchParams.max_price = filters.max_price
        if (filters.sort_by) searchParams.sort_by = filters.sort_by
        // Don't apply city or max_distance_km when showing everything
      }

      // Apply location filter based on locationFilter state
      if (locationFilter === 'city' && searchQuery.trim()) {
        searchParams.city = searchQuery.trim()
      } else if (locationFilter === 'neighbourhood' && userLocation) {
        // Neighbourhood: 5km radius from user location
        searchParams.max_distance_km = 5
      } else if (locationFilter === 'city' && userLocation) {
        // City: 20km radius from user location if no specific city is searched
        searchParams.max_distance_km = 20
      }
      // For locationFilter === 'all': no city or distance restrictions - show everything

      const results = await searchApi.searchAll(searchParams)
      setServices(results.services)
      setAnimals(results.animals)
    } catch (error) {
      toast.error(t('search.errors.loadServices'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    searchAll()
  }

  const handleFilterChange = (key: keyof ServiceSearchQuery, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value
    }))
  }

  const clearFilters = () => {
    setFilters({
      service_type: undefined,
      service_mode: undefined,
      animal_type: undefined,
      city: '',
      max_price: undefined,
      max_distance_km: undefined,
      sort_by: 'relevance',
      need: undefined
    })
    setLocationFilter('all') // Reset location filter to show everything
    setSearchQuery('') // Clear search query
  }

  // Re-search when content type or location filter changes
  useEffect(() => {
    searchAll()
  }, [contentType, locationFilter])

  const getServiceIcon = (serviceType: ServiceType) => {
    switch (serviceType) {
      case 'pension': return '🏠'
      case 'training': return '🎓'
      case 'walk': return '🚶'
      case 'walk_together': return '👥'
      case 'mate': return '💕'
      default: return '🐾'
    }
  }

  const getRatingStars = (rating: number | null) => {
    if (!rating) return <span className="text-gray-400 text-sm">{t('common.noRating')}</span>
    
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating.toFixed(1)})</span>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
            {t('search.title')}
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
            {t('search.subtitle')}
          </p>
        </div>

        {/* Search form */}
        <form onSubmit={handleSearch} className="mb-10">
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 border border-orange-100 dark:border-gray-700">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-orange-400 h-5 w-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search.placeholder')}
                  className="w-full pl-12 pr-4 py-4 bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-100 rounded-2xl focus:ring-2 focus:ring-orange-300 focus:border-orange-300 transition-all duration-200 text-lg placeholder-gray-500"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="px-6 py-4 bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-200 rounded-2xl hover:from-orange-200 hover:to-amber-200 flex items-center justify-center space-x-2 transition-all duration-200 text-gray-700 font-medium"
              >
                <Filter className="h-5 w-5" />
                <span>{t('search.filters')}</span>
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl"
              >
                {isLoading ? t('common.loading') : t('search.search')}
              </button>
            </div>
          </div>
        </form>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl mb-10 p-8 border border-orange-100 dark:border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold text-gray-900">{t('search.advancedFilters')}</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-orange-600 hover:text-orange-800 font-medium px-4 py-2 rounded-full hover:bg-orange-50 transition-all duration-200"
              >
                {t('search.clearFilters')}
              </button>
            </div>

            {/* Content Type Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                What are you looking for?
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setContentType('all')}
                  className={`px-4 py-3 rounded-2xl font-medium text-sm transition-colors ${
                    contentType === 'all'
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🔍 Everything
                </button>
                <button
                  type="button"
                  onClick={() => setContentType('services')}
                  className={`px-4 py-3 rounded-2xl font-medium text-sm transition-colors ${
                    contentType === 'services'
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🛠️ Services
                </button>
                <button
                  type="button"
                  onClick={() => setContentType('animals')}
                  className={`px-4 py-3 rounded-2xl font-medium text-sm transition-colors ${
                    contentType === 'animals'
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🐾 Animals
                </button>
              </div>
            </div>

            {/* Location Filter */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Location Filter
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setLocationFilter('all')}
                  className={`px-4 py-3 rounded-2xl font-medium text-sm transition-colors ${
                    locationFilter === 'all'
                      ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🌍 Show Everything
                </button>
                <button
                  type="button"
                  onClick={() => setLocationFilter('city')}
                  className={`px-4 py-3 rounded-2xl font-medium text-sm transition-colors ${
                    locationFilter === 'city'
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  disabled={!userLocation}
                >
                  🏙️ Show Only My City
                </button>
                <button
                  type="button"
                  onClick={() => setLocationFilter('neighbourhood')}
                  className={`px-4 py-3 rounded-2xl font-medium text-sm transition-colors ${
                    locationFilter === 'neighbourhood'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${!userLocation ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!userLocation}
                >
                  🏘️ Show Only My Neighbourhood
                </button>
              </div>
              {!userLocation && (
                <p className="text-sm text-gray-500 mt-2">
                  📍 Location access needed for city and neighbourhood filters
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(contentType === 'all' || contentType === 'services') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('search.serviceType')}
                    </label>
                    <select
                      value={filters.service_type || ''}
                      onChange={(e) => handleFilterChange('service_type', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">{t('search.allTypes')}</option>
                      {serviceTypes.map((type) => (
                        <option key={type} value={type}>
                          {t(`services.types.${type}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Need/Offer
                    </label>
                    <select
                      value={filters.need === undefined ? '' : filters.need.toString()}
                      onChange={(e) => handleFilterChange('need', e.target.value === '' ? undefined : e.target.value === 'true')}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Services</option>
                      <option value="false">🟢 Services Offered</option>
                      <option value="true">🔵 Services Needed</option>
                    </select>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('search.animalType')}
                </label>
                <select
                  value={filters.animal_type || ''}
                  onChange={(e) => handleFilterChange('animal_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('search.allAnimals')}</option>
                  {animalTypes.map((type) => (
                    <option key={type} value={type}>
                      {t(`animals.types.${type}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('search.maxPrice')}
                </label>
                <input
                  type="number"
                  value={filters.max_price || ''}
                  onChange={(e) => handleFilterChange('max_price', parseInt(e.target.value) || undefined)}
                  placeholder={t('search.enterMaxPrice')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('search.maxDistance')}
                </label>
                <select
                  value={filters.max_distance_km || ''}
                  onChange={(e) => handleFilterChange('max_distance_km', parseInt(e.target.value) || undefined)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('search.anyDistance')}</option>
                  <option value="1">1 km</option>
                  <option value="5">5 km</option>
                  <option value="10">10 km</option>
                  <option value="25">25 km</option>
                  <option value="50">50 km</option>
                  <option value="100">100 km</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('search.sortBy')}
                </label>
                <select
                  value={filters.sort_by || 'relevance'}
                  onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="relevance">{t('search.sortRelevance')}</option>
                  <option value="distance">{t('search.sortDistance')}</option>
                  <option value="recent">{t('search.sortRecent')}</option>
                  <option value="price_low">{t('search.sortPriceLow')}</option>
                  <option value="price_high">{t('search.sortPriceHigh')}</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={searchAll}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t('search.applyFilters')}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Results */}
          <div>
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                Results: {services.length} services, {animals.length} animals
                {locationFilter === 'all' && ' (showing everything)'}
                {locationFilter === 'city' && ' (city area)'}
                {locationFilter === 'neighbourhood' && ' (neighbourhood)'}
              </h2>
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">{t('common.loading')}</p>
              </div>
            ) : (services.length === 0 && animals.length === 0) ? (
              <div className="text-center py-12 bg-white rounded-lg">
                <Search className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('search.noResults')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('search.noResultsDesc')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {services.map((service) => (
                  <div key={service.id} className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-orange-50 hover:border-orange-100 transform hover:-translate-y-1">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <span className="text-3xl bg-gradient-to-br from-orange-100 to-amber-100 p-3 rounded-2xl">{getServiceIcon(service.service_type)}</span>
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold text-gray-900 mb-1">{service.title}</h3>
                            <span className={`px-4 py-2 text-sm font-semibold rounded-full ${
                              service.service_mode === 'offered' 
                                ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800' 
                                : 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800'
                            }`}>
                              {t(`services.modes.${service.service_mode}`)}
                            </span>
                          </div>
                        </div>

                        <div className="mb-2">
                          <p className="text-sm text-gray-600">
                            {t(`services.types.${service.service_type}`)} • {service.city}
                          </p>
                          {service.distance_km && (
                            <p className="text-sm text-gray-500">
                              <MapPin className="inline h-4 w-4 mr-1" />
                              {service.distance_km.toFixed(1)} km {t('search.away')}
                            </p>
                          )}
                        </div>

                        <p className="text-gray-700 mb-3 line-clamp-2">{service.description}</p>

                        <div className="flex flex-wrap gap-1 mb-3">
                          {service.animal_types.map((type) => (
                            <span
                              key={type}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                            >
                              {t(`animals.types.${type}`)}
                            </span>
                          ))}
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">
                              {t('search.providedBy')} <strong>{service.provider.username || t('user.anonymous')}</strong>
                            </p>
                            {getRatingStars(service.provider.rating_average || 0)}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">
                              {service.price_credits} <span className="text-sm font-normal">{t('common.credits')}</span>
                            </p>
                          </div>
                        </div>

                        <div className="mt-6 flex space-x-3">
                          <button className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-2xl hover:from-orange-600 hover:to-amber-600 flex items-center justify-center space-x-2 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl">
                            <MessageCircle className="h-5 w-5" />
                            <span>{t('search.contact')}</span>
                          </button>
                          <button className="px-4 py-3 bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-200 rounded-2xl hover:from-orange-200 hover:to-amber-200 flex items-center transition-all duration-200">
                            <Heart className="h-5 w-5 text-orange-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Animals */}
                {animals.length > 0 && (
                  <>
                    <div className="mt-8 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Animals ({animals.length})</h3>
                    </div>
                    {animals.map((animal) => (
                      <div key={animal.id} className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-orange-50 hover:border-orange-100 transform hover:-translate-y-1">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-4">
                              <span className="text-3xl bg-gradient-to-br from-orange-100 to-amber-100 p-3 rounded-2xl">🐾</span>
                              <div className="flex-1">
                                <h3 className="text-2xl font-bold text-gray-900 mb-1">{animal.name}</h3>
                                <span className="px-4 py-2 text-sm font-semibold rounded-full bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800">
                                  {animal.animal_type}
                                </span>
                              </div>
                            </div>

                            <div className="mb-3">
                              {animal.breed && (
                                <p className="text-sm text-gray-600">Breed: {animal.breed}</p>
                              )}
                              {animal.age && (
                                <p className="text-sm text-gray-600">Age: {animal.age} years</p>
                              )}
                            </div>

                            <p className="text-gray-700 mb-3 line-clamp-2">{animal.description || 'No description available'}</p>

                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-gray-600">
                                  Owner: <strong>{animal.user_id ? 'Available' : 'Anonymous'}</strong>
                                </p>
                              </div>
                            </div>

                            <div className="mt-6 flex space-x-3">
                              <button className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-2xl hover:from-orange-600 hover:to-amber-600 flex items-center justify-center space-x-2 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl">
                                <MessageCircle className="h-5 w-5" />
                                <span>Contact Owner</span>
                              </button>
                              <button className="px-4 py-3 bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-200 rounded-2xl hover:from-orange-200 hover:to-amber-200 flex items-center transition-all duration-200">
                                <Heart className="h-5 w-5 text-orange-600" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Map */}
          <div className="lg:sticky lg:top-8">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-orange-100">
              <div className="p-6 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{t('search.mapView')}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {locationLoading ? t('search.detectingLocation') : 
                       userLocation ? t('search.showingNearYou') : 
                       t('search.discoverServices')}
                    </p>
                  </div>
                  {locationLoading && (
                    <div className="flex items-center space-x-2 text-orange-600">
                      <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">{t('search.gettingLocation')}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4">
                <HereMap
                  center={userLocation || { lat: 52.520008, lng: 13.404954 }}
                  zoom={userLocation ? 12 : 6}
                  height="28rem"
                  markers={getAllMarkers()}
                  showCurrentLocation={true}
                  className="w-full rounded-2xl overflow-hidden"
                  key={`${userLocation ? `${userLocation.lat}-${userLocation.lng}` : 'default'}-${contentType}-${services.length}-${animals.length}`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchPage